/**
 * useFloorHistory — one CommandStack per editor session, exposed reactively.
 *
 * The stack instance lives in a `useState` INITIALIZER (stable identity, no
 * ref reads during render); undo/redo button state flows through
 * `useSyncExternalStore` on the stack's own listener seam, with the display
 * values decoded from the SNAPSHOT string (never read off the mutable stack
 * mid-render). Pushes/undos re-render ONLY consumers of this hook.
 *
 * @module floorplan/hooks/useFloorHistory
 * @category FloorPlan
 */

import { useCallback, useState, useSyncExternalStore } from 'react';

import { CommandStack } from '../history/CommandStack';
import type { FloorCommand } from '../history/types';
import { useFloorEngineStore } from '../stores/useFloorEngineStore';

export type FloorHistory = {
  stack: CommandStack;
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
  /** Execute a command against the engine store and record it. */
  execute: (command: FloorCommand) => void;
  /** Record a command whose mutation ALREADY happened (drag commits). */
  push: (command: FloorCommand) => void;
  undo: () => void;
  redo: () => void;
  clear: () => void;
};

const useFloorHistory = (): FloorHistory => {
  const [stack] = useState(() => new CommandStack());

  const subscribe = useCallback(
    (listener: () => void) => stack.subscribe(listener),
    [stack],
  );
  // Encoded snapshot string — a fresh object per call would loop the store.
  const getSnapshot = useCallback(
    () =>
      `${stack.canUndo ? 1 : 0}|${stack.canRedo ? 1 : 0}|${stack.undoLabel ?? ''}|${stack.redoLabel ?? ''}`,
    [stack],
  );
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const parts = snapshot.split('|');

  const execute = useCallback(
    (command: FloorCommand) => {
      stack.execute(command, useFloorEngineStore.getState());
    },
    [stack],
  );
  const push = useCallback(
    (command: FloorCommand) => {
      stack.push(command);
    },
    [stack],
  );
  const undo = useCallback(() => {
    stack.undo(useFloorEngineStore.getState());
  }, [stack]);
  const redo = useCallback(() => {
    stack.redo(useFloorEngineStore.getState());
  }, [stack]);
  const clear = useCallback(() => {
    stack.clear();
  }, [stack]);

  const undoLabel = parts[2] ?? '';
  const redoLabel = parts[3] ?? '';
  return {
    stack,
    canUndo: parts[0] === '1',
    canRedo: parts[1] === '1',
    undoLabel: undoLabel.length > 0 ? undoLabel : null,
    redoLabel: redoLabel.length > 0 ? redoLabel : null,
    execute,
    push,
    undo,
    redo,
    clear,
  };
};

export default useFloorHistory;
